import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/frontend/lib/utils";

type SlottableProps = {
  asChild?: boolean;
  className?: string;
  children: ReactNode;
};

export function NavigationRailStack({ asChild, className, children }: SlottableProps) {
  const Component = asChild ? Slot : "div";
  return <Component className={cn("space-y-8", className)}>{children}</Component>;
}

export function NavigationRailGroup({
  label,
  railAsChild,
  className,
  children,
}: SlottableProps & { label?: string; railAsChild?: boolean }) {
  const Rail = railAsChild ? Slot : "div";

  return (
    <section className={className}>
      {label && (
        <p className="mb-3 px-3 text-xs font-semibold text-zinc-500 dark:text-zinc-400">
          {label}
        </p>
      )}
      <Rail className="border-l border-zinc-200 dark:border-zinc-800">
        {children}
      </Rail>
    </section>
  );
}

export const NavigationRailItem = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & { asChild?: boolean; active?: boolean }
>(({ asChild, active, className, ...props }, ref) => {
  const Component = asChild ? Slot : "button";

  return (
    <Component
      ref={ref}
      aria-current={active ? "page" : undefined}
      className={cn(
        "-ml-px flex min-h-8 w-[calc(100%+1px)] items-center border-l-2 px-3 py-1.5 text-left text-sm leading-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-600",
        active
          ? "border-emerald-600 bg-zinc-100 font-semibold text-zinc-950 dark:border-[#d7ff64] dark:bg-zinc-900 dark:text-zinc-100"
          : "border-transparent font-medium text-zinc-600 hover:border-zinc-400 hover:text-zinc-950 dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:text-zinc-100",
        className,
      )}
      {...props}
    />
  );
});
NavigationRailItem.displayName = "NavigationRailItem";
